import { Location } from '@angular/common';
import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from '@angular/fire/compat/firestore';
import { Timestamp, serverTimestamp } from '@angular/fire/firestore';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { intersects, objectify } from 'radash';
import {
  EMPTY,
  defer,
  map,
  Observable,
  Subject,
  catchError,
  debounceTime,
  from,
  mergeMap,
  of,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { Group, GroupType } from '../../models/group.model';
import { SchoolMemberType } from '../../models/member.school.model';
import { Session, SessionStatus } from '../../models/session.school.model';
import { UserModel } from '../../models/user.model';
import { SchoolSessionService } from '../../services/session.school.service';
import * as groupActions from '../actions/group.actions';
import * as sessionActions from '../actions/session.school.actions';
import * as userActions from '../actions/user.actions';
import { AppState } from '../appstate';
import * as groupSelector from '../selectors/group.selector';
import * as sessionSelector from '../selectors/session.school.selector';

@Injectable()
export class SchoolSessionEffects implements OnDestroy {
  actions$ = inject(Actions);
  store = inject(Store<AppState>);
  logger: NGXLogger = inject(NGXLogger);
  sessionService: SchoolSessionService = inject(SchoolSessionService);
  toastr: ToastrService = inject(ToastrService);
  afs: AngularFirestore = inject(AngularFirestore);
  router: Router = inject(Router);
  route: ActivatedRoute = inject(ActivatedRoute);
  location: Location = inject(Location);

  user: UserModel | null = null;

  private logout$ = new Subject<void>();

  constructor() {
    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.logout$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnDestroy(): void {
    this.logout$.next();
    this.logout$.complete();
  }

  // Effect to fetch Pending Sessions
  getSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sessionActions.getSessions),
      debounceTime(500),
      switchMap(
        ({
          groupId,
          academicYear,
          gradeId,
          teacherId,
          fromDate,
          status,
          lastRecord,
        }) =>
          defer(() => {
            this.logger.debug(
              `getSessions$: groupId: ${groupId}, academicYear: ${academicYear} (${typeof academicYear}), gradeId: ${gradeId}, teacherId: ${teacherId}, fromDate: ${fromDate}, status: ${status}, lastRecord: ${lastRecord?.toDate()}`,
            );
            this.logout$.next();
            let query: AngularFirestoreCollection<Session> = this.afs
              .collection('groups')
              .doc(groupId)
              .collection('sessions', (ref) => {
                let reference: firebase.default.firestore.Query = ref;

                reference = reference.where('academicYear', '==', academicYear);

                reference = reference.where(
                  'sessionStatus',
                  'in',
                  status ?? [SessionStatus.active],
                );

                if (gradeId) {
                  reference = reference.where('gradeId', '==', gradeId);
                }

                if (teacherId) {
                  reference = reference.where('teacherId', '==', teacherId);
                }

                if (fromDate) {
                  reference = reference.where('startDate', '>=', fromDate);
                }

                // Ordering and Pagination
                reference = reference.orderBy('startDate', 'desc').limit(10);

                if (lastRecord) {
                  reference = reference.startAfter(lastRecord);
                }

                return reference;
              });
            return query.get();
          }).pipe(
            // Handle errors per request so the effect keeps running.
            map((snapshot) => {
              this.logger.debug(
                `getSessions$: Snapshot returned with ${snapshot.size} sessions`,
              );
              const sessions = snapshot.docs.map((doc) => ({
                ...(doc.data() as Session),
                id: doc.id,
              }));
              return sessionActions.addSessionsToStore({ sessions });
            }),
            catchError((error) => {
              this.logger.error(`Error fetching sessions: ${error}`);
              this.toastr.error(
                'An error occurred. Please try again later.',
                'Error Fetching Sessions',
              );
              return of(sessionActions.getSessionsFailure({ error }));
            }),
          ),
      ),
    ),
  );

  // Effect to check if the selected route is to a school, and the logged in use has a memberType of teacher
  // If so, fetch only the sessions where teacherId is the logged in user
  // fetchSessions$: Observable<Action> = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(groupActions.select),
  //     withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
  //     exhaustMap(([{ groupId }, group]) => {
  //       if (group == null || group?.groupType != GroupType.school) return EMPTY;
  //       this.logger.debug(`fetchSessions$: School groupId: ${group?.id}`);
  //       return this.store
  //         .select(
  //           sessionSelector.getSessionsCountForSelectedGroup(
  //             group.academicYear,
  //           ),
  //         )
  //         .pipe(
  //           take(1),
  //           exhaustMap((count) => {
  //             if (count == 0) {
  //               this.logger.debug(
  //                 `fetchSessions$: No Sessions found. Fetching...`,
  //               );
  //               return this.store
  //                 .select(groupSelector.getMyRolesInSelectedGroup)
  //                 .pipe(
  //                   take(1),
  //                   switchMap((roles) => {
  //                     if (roles?.management) {
  //                       this.logger.debug(
  //                         `fetchSessions$: User in Management Role. Fetching all sessions`,
  //                       );
  //                       return of(
  //                         sessionActions.getSessions({
  //                           groupId: group.id,
  //                           academicYear: group.academicYear,
  //                           // fromDate: Timestamp.fromDate(
  //                           //   moment().subtract(7, 'days').toDate()
  //                           // ),
  //                           lastRecord: undefined,
  //                         }),
  //                       );
  //                     } else if (roles?.faculty) {
  //                       // console.log(
  //                       //   `fetchSessions$: User in Teacher Role. Fetching all grade sessions`
  //                       // );
  //                       return of(
  //                         sessionActions.getSessions({
  //                           groupId: group.id,
  //                           academicYear: group.academicYear,
  //                           gradeId: group.gradeId,
  //                           // fromDate: Timestamp.fromDate(
  //                           //   moment().subtract(7, 'days').toDate()
  //                           // ),
  //                           lastRecord: undefined,
  //                         }),
  //                       );
  //                     } else if (roles?.student) {
  //                       this.logger.debug(
  //                         `fetchSessions$: User in Student Role. Fetching all grade sessions`,
  //                       );
  //                       return of(
  //                         sessionActions.getSessions({
  //                           groupId: group.id,
  //                           academicYear: group.academicYear,
  //                           gradeId: group.gradeId,
  //                           // fromDate: Timestamp.fromDate(
  //                           //   moment().subtract(30, 'days').toDate()
  //                           // ),
  //                           lastRecord: undefined,
  //                         }),
  //                       );
  //                     }
  //                     return EMPTY;
  //                   }),
  //                 );
  //             } else {
  //               // console.log(
  //               //   `fetchSessions$: Sessions already fetched. Exiting...`
  //               // );
  //               return EMPTY;
  //             }
  //           }),
  //         );
  //     }),
  //   ),
  // );

  getGroupSessionStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.select, sessionActions.getSessionStats),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId }, group]) => {
        this.logout$.next();
        if (group?.groupType !== GroupType.school) return EMPTY;
        if (
          !intersects(group?.memberType as SchoolMemberType[], [
            SchoolMemberType.president,
            SchoolMemberType.principal,
          ])
        ) {
          return EMPTY;
        }
        if (group == undefined) return EMPTY;
        // console.log(
        //   `getGroupSessionStats$: Getting session stats for groupId: ${groupId}`
        // );
        return this.afs
          .collection('groups')
          .doc(group.id)
          .collection<any>('session_stats')
          .get()
          .pipe(takeUntil(this.logout$))
          .pipe(
            switchMap((snapshot) => {
              // console.log(
              //   `getGroupSessionStats$: Snapshot Returned: Data Exists: ${!snapshot.empty}`
              // );
              if (snapshot.empty) return EMPTY;
              // console.log(`getGroupSessionStats$ :: Stats found`);
              const stats = snapshot.docs.map((doc) => {
                let record = doc.data();
                return {
                  ...record,
                };
              });

              // console.log(
              //   `getGroupSessionStats$ :: Group Session Stats found: ${JSON.stringify(
              //     objectify(stats, (f) => f.gradeId)
              //   )}`
              // );
              const g: Group = {
                id: group.id,
                sessionStats: objectify(stats, (f) => f.gradeId),
                sessionStatsLoaded: true,
              } as Group;
              return of(groupActions.getSessionStatsDone({ group: g }));
            }),
            catchError((error) => {
              this.logger.error(
                `getGroupSessionStats$ :: Error: ${JSON.stringify(error)}`,
              );
              return of(groupActions.getSessionStatsDone({ group: null }));
            }),
          );
      }),
    ),
  );

  getGradeSessionStats$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sessionActions.getSessionStats),
      withLatestFrom(this.store.select(groupSelector.getSelectedGroup)),
      switchMap(([{ groupId, gradeId }, group]) => {
        this.logout$.next();
        if (group?.groupType !== GroupType.school) return EMPTY;
        if (
          !intersects(group?.memberType as SchoolMemberType[], [
            SchoolMemberType.teacher,
          ])
        ) {
          return EMPTY;
        }
        if (group == undefined) return EMPTY;
        // console.log(
        //   `getGradeSessionStats$: Getting session stats for: ${gradeId}_${group.academicYear}`
        // );
        return this.afs
          .collection('groups')
          .doc(group.id)
          .collection<any>('session_stats')
          .doc(`${gradeId}_${group.academicYear}`)
          .get()
          .pipe(takeUntil(this.logout$))
          .pipe(
            switchMap((snapshot) => {
              // console.log(
              //   `getGradeSessionStats$: Snapshot Returned: Data Exists: ${!snapshot.exists}`
              // );
              if (!snapshot.exists) return EMPTY;
              let stats = snapshot.data();
              // console.log(
              //   `getGradeSessionStats$ :: Stats found; ${JSON.stringify(
              //     objectify(stats, (f: any) => f.gradeId)
              //   )}`
              // );
              const g: Group = {
                id: group.id,
                sessionStats: { [gradeId]: stats },
                sessionStatsLoaded: true,
              } as Group;
              return of(groupActions.getSessionStatsDone({ group: g }));
            }),
          );
      }),
      catchError((error) => {
        this.logger.error(
          `getGradeSessionStats$ :: Error: ${JSON.stringify(error)}`,
        );
        return of(groupActions.getSessionStatsDone({ group: null }));
      }),
    ),
  );

  // Effect to create a new session
  create$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sessionActions.create),
      switchMap(({ groupId, session }) => {
        const createSession = {
          ...session,
          created: serverTimestamp() as Timestamp,
          modified: serverTimestamp() as Timestamp,
        };

        return from(this.sessionService.create(groupId, createSession)).pipe(
          switchMap((sessionId) => {
            const localSession: Session = {
              ...session,
              id: sessionId,
              created: Timestamp.now(),
              modified: Timestamp.now(),
            };
            return of(sessionActions.createSuccess({ session: localSession }));
          }),
          catchError((error) => {
            this.logger.error(`Error creating session: ${error}`);
            return of(sessionActions.createFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to update a session
  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sessionActions.update),
      switchMap(({ groupId, sessionId, session }) => {
        const updateSession = {
          ...session,
          modified: serverTimestamp() as Timestamp,
        };
        return from(
          this.sessionService.update(groupId, sessionId, updateSession),
        ).pipe(
          switchMap(() => {
            const localSession: Session = {
              ...session,
              id: sessionId,
              modified: Timestamp.now(),
            };
            this.toastr.success(
              'The session details have been updated successfully.',
              'Session Updated',
            );
            return of(sessionActions.updateSuccess({ session: localSession }));
          }),
          catchError((error) => {
            // console.error(`Error updating session: ${error}`);
            this.toastr.error(
              `An error occurred while updating the session details. Please try again later.`,
              'Error Updating Session',
            );
            return of(sessionActions.updateFailure({ error }));
          }),
        );
      }),
    ),
  );

  // Effect to update a session status
  updateStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sessionActions.updateStatus),
      switchMap(({ groupId, sessionId, session }) => {
        return from(
          this.sessionService.update(groupId, sessionId, {
            ...session,
            modified: serverTimestamp() as Timestamp,
          }),
        ).pipe(
          switchMap(() => {
            const localSession: Session = {
              ...session,
              id: sessionId,
              modified: Timestamp.now(),
            };
            return of(
              sessionActions.updateStatusSuccess({ session: localSession }),
            );
          }),
          catchError((error) => {
            this.logger.error(`Error updating session: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Updating Session Status',
            );
            return of(sessionActions.updateStatusFailure({ error }));
          }),
        );
      }),
    ),
  );

  // effect to delete a session
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sessionActions.deleteAction),
      switchMap(({ groupId, id }) =>
        this.sessionService
          .delete(groupId, id)
          .then(() => {
            this.toastr.success(
              'The session has been deleted successfully.',
              'Session Deleted',
            );
            return sessionActions.deleteSuccess({ id: id });
          })
          .catch((error: any) => {
            this.logger.error(`Error deleting session: ${error}`);
            this.toastr.error(
              `An error occurred, '${error}'. Please try again later.`,
              'Error Deleting Session',
            );
            return sessionActions.deleteFailure({ error });
          }),
      ),
    ),
  );

  clearGroupSessions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(groupActions.clearFromStore),
      switchMap(({ id }) =>
        this.store.select(sessionSelector.getSessionIdsByGroup(id)).pipe(
          take(1),
          switchMap((sessions) => {
            if (sessions.length > 0) {
              return of(sessionActions.clearFromStore({ ids: sessions }));
            }
            return EMPTY;
          }),
        ),
      ),
    ),
  );

  clearSessions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(userActions.logoutSuccess),
      switchMap(() => {
        this.logout$.next();
        return of(sessionActions.clear());
      }),
    ),
  );
}
